import { useExcelExport } from "@/hooks/useExcelExport";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridPagination, useGridApiContext } from "@mui/x-data-grid-pro";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  convertFullWidthDigitsAndHyphenToHalfWidth,
  convertTypeEnglishToJapanese,
  formatArea,
} from "../utils";
import { useEffect, useState } from "react";
import type { PropertySelectionRow } from "../types";

export const CustomFooter: React.FC<{ rows: PropertySelectionRow[] }> = ({
  rows,
}) => {
  const [sumArea, setSumArea] = useState<number>(0);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const isMobile = useIsMobile();

  // エクセルエクスポートで必要な関数を取得。
  const { downloadAsExcelFile, getFeedGridData, makeExcelUint8Array } =
    useExcelExport();

  // feedGridの内部データを読み書きするためのハンドルみたいなものを取得
  const apiRef = useGridApiContext();

  /**
   * ピンのデータをエクセルでエクスポートする。
   */
  const handleExportExcel = (): void => {
    if (rows.length === 0) return;
    const arrayOfArray = getFeedGridData(apiRef);
    const formatData = arrayOfArray.map((element, index) => {
      // Action列は削除。
      element.pop();

      // 地番・家屋番号を半角に変換
      element[3] = convertFullWidthDigitsAndHyphenToHalfWidth(element[3]);

      // 所在の丁目を全て半角に変換する
      element[2] = convertFullWidthDigitsAndHyphenToHalfWidth(element[2]);

      // ヘッダーの不動産種別は日本語変換しない。
      if (index !== 0) element[0] = convertTypeEnglishToJapanese(element[0]);

      return element;
    });

    const data = makeExcelUint8Array(formatData, "地図検索");

    downloadAsExcelFile(data, "R.E.DATA_地図検索_ピンデータ");
    setExportDialogOpen(false);
  };

  useEffect(() => {
    const sum = rows.reduce((acc: number, row: PropertySelectionRow) => {
      if (typeof row.area === "number" && row.selected) {
        return acc + row.area;
      }
      return acc;
    }, 0);
    setSumArea(sum);
  }, [rows]);

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Divider />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 1, gap: 1, minHeight: 44 }}
      >
        <Tooltip title={`合計推計地積 ${formatArea(sumArea)}㎡`}>
          <Typography
            variant="body2"
            noWrap
            sx={{ minWidth: 0, fontSize: "0.75rem" }}
          >
            合計推計地積 {formatArea(sumArea)}㎡
          </Typography>
        </Tooltip>
        <Tooltip title="エクセルでエクスポート">
          <Box component="span" sx={{ flexShrink: 0 }}>
            <Button
              aria-label="エクセルでエクスポート"
              disabled={rows.length === 0}
              onClick={() => {
                setExportDialogOpen(true);
              }}
              size="small"
              startIcon={<FileDownloadIcon />}
              sx={{ flexShrink: 0, minHeight: 44, fontSize: "0.75rem" }}
            >
              Excel
            </Button>
          </Box>
        </Tooltip>
      </Stack>
      <Divider />
      <GridPagination
        labelRowsPerPage=""
        SelectProps={{
          inputProps: { "aria-label": "ページあたりの表示件数" },
          renderValue: (value) => `${String(value)}件/ページ`,
        }}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} / ${count === -1 ? `${to}以上` : count}`
        }
        sx={{
          fontSize: "0.6875rem",
          "& .MuiTablePagination-toolbar": {
            minHeight: 44,
            justifyContent: "flex-end",
            px: 0.5,
          },
          "& .MuiTablePagination-spacer": { display: "none" },
          "& .MuiTablePagination-selectLabel": {
            display: "none",
          },
          "& .MuiTablePagination-toolbar .MuiTablePagination-input": {
            display: "inline-flex",
            fontSize: "inherit",
            ml: 0,
            mr: "auto",
          },
          "& .MuiTablePagination-select": { pl: 0.5, pr: "20px !important" },
          "& .MuiTablePagination-displayedRows": {
            fontSize: "inherit",
            my: 0,
            whiteSpace: "nowrap",
          },
          "& .MuiTablePagination-toolbar .MuiTablePagination-actions": {
            ml: 0,
            "& .MuiIconButton-root": { width: 44, height: 44, p: 1 },
            "& .MuiSvgIcon-root": { fontSize: 20 },
          },
        }}
      />
      <Dialog
        open={exportDialogOpen}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setExportDialogOpen(false);
        }}
        fullScreen={isMobile}
        fullWidth
        maxWidth="xs"
        aria-labelledby="property-list-export-title"
      >
        <DialogTitle id="property-list-export-title">
          Excelをダウンロード
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            物件リストをExcelファイルでダウンロードします。よろしいですか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setExportDialogOpen(false);
            }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            disabled={rows.length === 0}
            onClick={handleExportExcel}
          >
            ダウンロード
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
